import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { FormProvider, useForm } from "react-hook-form";
import {
  PaziaFormButton,
  IDropdownOption,
  PaziaInput,
  PaziaMultiSelect,
  PaziaTextarea,
} from "components/atoms";
import { PaziaBox } from "components/atoms";
import { PlaceApi } from "api";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { optionalCoordinate, optionalEmail, optionalPhone, optionalPostCode } from "utils/contactValidation";

interface CreatePlaceFormProps {
  tags: IDropdownOption[];
  onSubmit: (data: PlaceFormValues) => void;
  data?: PlaceApi;
}

export const PlaceForm = ({ tags, onSubmit, data }: CreatePlaceFormProps) => {
  const methods = useForm<PlaceFormValues>({
    resolver: yupResolver(schema),
    context: { saved: data },
    defaultValues: {
      name: data?.name,
      description: data?.description ?? "",
      address: data?.address,
      city: data?.city,
      postCode: data?.postCode,
      latitude: data?.latitude,
      longitude: data?.longitude,
      directions: data?.directions,
      email: data?.email,
      phone: data?.phone,
      website: data?.website,

      //@ts-ignore TODO
      tags: (data?.tags ?? []).map((x) => x.id.toString()),
    },
  });
  const { handleSubmit } = methods;

  return (
    <PaziaBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex grid-cols-2 flex-col gap-2 md:grid md:gap-8">
          <div className="grid auto-rows-min gap-4">
            <PaziaInput name={PLACE_FORM_NAME.NAME} label="Nazwa" type="text" required />
            <PaziaFromQuill name={PLACE_FORM_NAME.DESCRIPTION} label="Opis" />
            <PaziaMultiSelect label="Tagi" options={tags} name={PLACE_FORM_NAME.TAGS} />
          </div>
          <div className="flex auto-rows-min grid-cols-2 flex-col gap-2 md:grid md:gap-4">
            <PaziaInput name={PLACE_FORM_NAME.EMAIL} label="Email" type="text" />
            <PaziaInput name={PLACE_FORM_NAME.PHONE} label="Telefon" type="text" />
            <PaziaInput name={PLACE_FORM_NAME.WEBSITE} label="Strona internetowa" type="text" className="col-span-2" />
            <PaziaInput name={PLACE_FORM_NAME.ADDRESS} label="Adres" type="text" className="col-span-2" />
            <PaziaInput name={PLACE_FORM_NAME.CITY} label="Miasto" type="text" />
            <PaziaInput name={PLACE_FORM_NAME.POSTCODE} label="Kod pocztowy" type="text" />
            <PaziaInput name={PLACE_FORM_NAME.LATITUDE} label="Szerokość geograficzna" type="text" />
            <PaziaInput name={PLACE_FORM_NAME.LONGITUDE} label="Długość geograficzna" type="text" />
            <PaziaTextarea name={PLACE_FORM_NAME.DIRECTIONS} label="Wskazówki dojazdu" className="col-span-2" />
          </div>

          <div className="col-start-2 flex justify-end">
            <PaziaFormButton title="Zapisz" type="success" />
          </div>
        </form>
      </FormProvider>
    </PaziaBox>
  );
};

const PLACE_FORM_NAME = {
  NAME: "name",
  DESCRIPTION: "description",
  ADDRESS: "address",
  CITY: "city",
  TAGS: "tags",
  POSTCODE: "postCode",
  LATITUDE: "latitude",
  LONGITUDE: "longitude",
  DIRECTIONS: "directions",
  EMAIL: "email",
  PHONE: "phone",
  WEBSITE: "website",
} as const;

const schema = yup
  .object({
    [PLACE_FORM_NAME.NAME]: yup.string().required(),
    [PLACE_FORM_NAME.DESCRIPTION]: yup.string(),
    [PLACE_FORM_NAME.ADDRESS]: yup.string(),
    [PLACE_FORM_NAME.CITY]: yup.string(),
    [PLACE_FORM_NAME.TAGS]: yup.array().of(yup.string()),
    [PLACE_FORM_NAME.POSTCODE]: optionalPostCode(),
    [PLACE_FORM_NAME.LATITUDE]: optionalCoordinate(-90, 90),
    [PLACE_FORM_NAME.LONGITUDE]: optionalCoordinate(-180, 180),
    [PLACE_FORM_NAME.DIRECTIONS]: yup.string(),
    [PLACE_FORM_NAME.EMAIL]: optionalEmail(),
    [PLACE_FORM_NAME.PHONE]: optionalPhone(),
    [PLACE_FORM_NAME.WEBSITE]: yup.string(),
  })
  .required();
export type PlaceFormValues = yup.InferType<typeof schema>;
